import { inject, Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import {
    PaymentsProcessorAuthMode,
    PaymentsProcessorIdentityHint
} from '@bayclubs/payments-processor-office-launcher';
import { Observable, of, throwError } from 'rxjs';

/**
 * Supplies the credentials the payments-processor launcher needs.
 *
 * The hosted app has its own Entra ID registration and signs itself in, so no token goes into
 * the iframe URL. All we owe it is an identity hint, because a third-party iframe cannot discover
 * the signed-in employee on its own and Entra will not answer a prompt=none request for an
 * ambiguous account. A hint is a username and a session id - not a credential.
 */
@Injectable({ providedIn: 'root' })
export class PaymentsProcessorLaunchAuthService {
    private readonly msal = inject(MsalService);

    readonly authMode: PaymentsProcessorAuthMode = 'msal';

    /** Always empty - nothing bearer-shaped goes into the iframe URL. */
    readonly accessToken = '';

    readonly identityHintProvider = (): Observable<PaymentsProcessorIdentityHint> => this.resolveHint();

    private resolveHint(): Observable<PaymentsProcessorIdentityHint> {
        const account = this.msal.instance.getActiveAccount() ?? this.msal.instance.getAllAccounts()[0];

        if (!account) {
            return throwError(() => new Error('No signed-in account to build an identity hint from'));
        }

        // sid names the exact Entra session and is the stronger hint, but it only appears here when
        // the "sid" optional claim is enabled on this app registration - so it is sent when present
        // and the username carries the request otherwise.
        const sid = account.idTokenClaims?.sid;

        return of({
            loginHint: account.username,
            ...(sid ? { sid } : {})
        });
    }
}
